/**
 * Sound utility functions for Global Thermo III
 * Military-themed audio feedback for UI interactions
 */

/**
 * Play military console button click sound
 * @param {number} volume - Volume level (0.0 to 1.0), default 0.4
 */
export const playConsoleClick = (volume = 0.4) => {
  const click = new Audio('/assets/audio/nuke-button-press.mp3');
  click.volume = volume;
  click.play().catch(e => console.log('Console click sound failed:', e));
};

/**
 * Play hover boop sound (already used in game)
 * @param {number} volume - Volume level (0.0 to 1.0), default 0.3
 */
export const playHoverBoop = (volume = 0.3) => {
  const boop = new Audio('/assets/audio/hover-boop.mp3');
  boop.volume = volume;
  boop.play().catch(e => console.log('Hover boop failed:', e));
};

/**
 * Play error beep sound for invalid actions
 * @param {number} volume - Volume level (0.0 to 1.0), default 0.5
 */
export const playErrorBeep = (volume = 0.5) => {
  const error = new Audio('/assets/audio/error-beep.mp3');
  error.volume = volume;
  error.play().catch(e => console.log('Error beep failed:', e));
};



/**
 * 📱 iOS / mobile audio unlocker.
 * iOS Safari, Chrome, and Edge block `new Audio().play()` unless triggered
 * inside a fresh synchronous user-gesture handler. Audio created later (e.g.,
 * inside a useEffect during an animation) silently fails with NotAllowedError.
 *
 * This function plays + immediately pauses a tiny silent buffer on the FIRST
 * real user click anywhere in the app. That single gesture "unlocks" the
 * audio policy for the rest of the session, so subsequent programmatic
 * .play() calls (airstrike sirens, eject screams, etc.) actually fire.
 *
 * Safe to call multiple times — only runs once thanks to the `unlocked` flag.
 */
let audioUnlocked = false;
export const unlockAudioOnFirstGesture = () => {
  if (audioUnlocked || typeof window === 'undefined') return;

  const unlock = () => {
    if (audioUnlocked) return;
    audioUnlocked = true;

    // Silent dummy audio — 1 frame of base64-encoded silence
    try {
      const silent = new Audio(
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='
      );
      silent.volume = 0;
      silent.play().then(() => silent.pause()).catch(() => {});
    } catch (e) { /* ignore */ }

    // Also resume any Web Audio context if one exists
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      }
    } catch (e) { /* ignore */ }

    console.log('🔓 Audio unlocked for session');

    // Self-clean — listeners only need to fire once
    window.removeEventListener('touchstart', unlock);
    window.removeEventListener('click', unlock);
  };

  window.addEventListener('touchstart', unlock, { once: true, passive: true });
  window.addEventListener('click', unlock, { once: true });
};
